import {
  Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OperationPlanifiee } from '../../../models/operation-planifiee.model';
import { OperationReelle } from '../../../models/operation-reelle.model';

interface SommeMensuelle {
  mois: string;
  nombreOperations: number;
  montant: number;
}

@Component({
  selector: 'app-popup-historique-planifiee',
  imports: [CommonModule],
  templateUrl: './popup-historique-planifiee.component.html',
  styleUrl: './popup-historique-planifiee.component.scss',
})
export class PopupHistoriquePlanifieeComponent implements OnChanges {
  @Input() visible = false;
  @Input() operation: OperationPlanifiee | null = null;
  @Output() fermer = new EventEmitter<void>();
  @ViewChild('fermerBouton') fermerBouton?: ElementRef<HTMLButtonElement>;

  periode: '12_MOIS' | 'TOUT' = '12_MOIS';
  affichage: 'MENSUEL' | 'DETAIL' = 'MENSUEL';
  private aujourdHui = new Date();

  ngOnChanges(changes: SimpleChanges): void {
    if (this.visible && (changes['visible'] || changes['operation'])) {
      this.periode = '12_MOIS';
      this.affichage = 'MENSUEL';
      this.aujourdHui = new Date();
      setTimeout(() => {
        if (this.visible) this.fermerBouton?.nativeElement.focus();
      }, 0);
    }
  }

  get operations(): OperationReelle[] {
    const aujourdHui = this.dateISO(this.aujourdHui);
    const debut = new Date(this.aujourdHui.getFullYear() - 1, this.aujourdHui.getMonth(), 1);
    const dernierJour = new Date(debut.getFullYear(), debut.getMonth() + 1, 0).getDate();
    debut.setDate(Math.min(this.aujourdHui.getDate(), dernierJour));
    const dateDebut = this.dateISO(debut);

    return (this.operation?.operationsReelles ?? [])
      .filter((op) => op.dateOperation <= aujourdHui &&
        (this.periode === 'TOUT' || op.dateOperation >= dateDebut))
      .slice()
      .sort((a, b) => b.dateOperation.localeCompare(a.dateOperation) || b.id - a.id);
  }

  montantSigne(operation: OperationReelle): number {
    const type = operation.type ?? this.operation?.type;
    return type === 'SORTIE' ? -Number(operation.montant) : Number(operation.montant);
  }

  get total(): number {
    return this.sommesMensuelles.reduce((total, mois) => total + Math.round(mois.montant * 100), 0) / 100;
  }

  get sommesMensuelles(): SommeMensuelle[] {
    const sommes = new Map<string, { nombreOperations: number; montantCentimes: number }>();
    for (const operation of this.operations) {
      const mois = operation.dateOperation.slice(0, 7);
      const somme = sommes.get(mois) ?? { nombreOperations: 0, montantCentimes: 0 };
      somme.nombreOperations++;
      somme.montantCentimes += Math.round(this.montantSigne(operation) * 100);
      sommes.set(mois, somme);
    }
    return Array.from(sommes, ([mois, somme]) => ({
      mois, nombreOperations: somme.nombreOperations, montant: somme.montantCentimes / 100,
    })).sort((a, b) => b.mois.localeCompare(a.mois));
  }

  get moisPourMoyenne(): SommeMensuelle[] {
    const moisCourant = this.dateISO(this.aujourdHui).slice(0, 7);
    return this.sommesMensuelles.filter((mois) => mois.mois < moisCourant);
  }

  get moyenneMensuelle(): number | null {
    const mois = this.moisPourMoyenne;
    if (!mois.length) return null;
    const totalCentimes = mois.reduce((total, somme) => total + Math.round(somme.montant * 100), 0);
    return totalCentimes / mois.length / 100;
  }

  get moyenneOperation(): number | null {
    const nombreOperations = this.operations.length;
    return nombreOperations ? this.total / nombreOperations : null;
  }

  moyenPaiement(operation: OperationReelle): string {
    const labels: Record<string, string> = {
      CB: 'CB', CB_DIFFERE: 'CB différée', CHEQUE: 'Chèque',
      VIREMENT: 'Virement', PRELEVEMENT: 'Prélèvement',
    };
    return operation.moyenPaiement
      ? labels[operation.moyenPaiement] ?? operation.moyenPaiement
      : '—';
  }

  gererClavier(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.fermer.emit();
    } else if (event.key === 'Tab') {
      const panel = event.currentTarget as HTMLElement;
      const boutons = Array.from(panel.querySelectorAll<HTMLButtonElement>('button'));
      const premier = boutons[0];
      const dernier = boutons[boutons.length - 1];
      if (event.shiftKey && event.target === premier) {
        event.preventDefault();
        dernier?.focus();
      } else if (!event.shiftKey && event.target === dernier) {
        event.preventDefault();
        premier?.focus();
      }
    }
  }

  private dateISO(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
